/**
 * JewelleryTagViewer — the shared, reusable tag UI used by BOTH:
 *   • JewelleryTagDialog (in-app, authed) and
 *   • JewelleryTagView   (public QR-scan page, no auth).
 *
 * Responsibilities:
 *   - 3 selectable layouts (Classic Card / Hang Tag / Detailed Wide)
 *   - Raw-material view toggle: Expanded (one line per consumed entry, with
 *     notes) OR Collapsed (totalled per classification — carats + pieces)
 *   - A QR code that links to the PUBLIC viewer page ( /t/{id} ) so scanning
 *     opens the full tag on any phone
 *   - Print the full tag at its exact physical size
 *   - Print ONLY the QR code (small label)
 *
 * Tags are built with inline styles so a bare print window reproduces them
 * pixel-for-pixel (1 CSS px = 1/96in, matching the @page inch sizes).
 */

import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { isPublicTagHost } from '@/lib/publicTagHost';
import { useLabelPresets, saveLabelPresets, saveCompanyPresets } from '@/lib/tagPresets';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Printer, QrCode, Rows3, List, Ban, FileSpreadsheet, ArrowUpDown } from 'lucide-react';
import { formatGrams, formatCarats } from '@/lib/format';
import { sortByMaterialHierarchy, strictTagOrder } from '@/lib/materialHierarchy';
import { toast } from 'sonner';
import { applyTagRules, getTagRules, getTagClassOrder, useTagRules } from '@/lib/tagRules';
import { downloadTagsExcel } from '@/lib/tagExcel';
import { TagMergePreview } from '@/notes/tagws/TagMergePreview';
import { TagLineFixDialog } from '@/notes/tagws/TagLineFixDialog';
import { TagReorderLinesDialog } from '@/notes/tagws/TagReorderLinesDialog';
import { notesApi } from '@/lib/notesApi';
import { rateCardById, useRateCards } from '@/lib/rateCards';
import { useGoldRate, currentGold } from '@/lib/goldRate';
import { PricingControls, usePricing, pricedMaterials, ensureRates, pricingOn, inr } from '@/lib/tagPricing';

// ── Print-safe palette (mirrors ERP tokens; hard-coded so the bare print
//    window / public page need no app CSS). ──────────────────────────────
export const C = {
  ink: '#0B1B2B',
  emerald: '#047857',
  amber: '#92400E',
  violet: '#4B2E83',
  divider: '#E7E3DA',
  muted: '#64748B',
  slate50: '#F8FAFC',
  line: '#E2E8F0',
  white: '#FFFFFF',
};
import { printFontCss } from '@/lib/tagFont';
import { applyMatHandling, sepRows, tickedFor } from '@/lib/tagMatHandling';
import { TagMatOptToggles } from './TagMatOptToggles';

const SANS = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO = "'Inter', ui-sans-serif, system-ui, sans-serif";

const matColor = (t) => (
  t === 'diamond' ? '#0369A1'
    : t === 'color_stone' ? '#BE123C'
      : t === 'pearl' ? '#475569'
        : C.violet
);

export const TAG_LAYOUTS = [
  { id: 'classic', label: 'Classic Card', w: 336, page: '3.5in 2in', qr: 52, desc: 'Business-card size · landscape' },
  { id: 'hangtag', label: 'Hang Tag', w: 216, page: '2.25in 3.5in', qr: 60, desc: 'Classic jewellery tag · portrait' },
  { id: 'detailed', label: 'Detailed', w: 384, page: '4in 3in', qr: 64, desc: 'More room for materials · wide' },
  // Grid layouts size themselves to their content (`fit: true`). `w` here is
  // only a nominal ceiling used for preview scaling; the real printed width is
  // measured from the rendered node so there is no dead gap on wide columns.
  { id: 'grid2', label: 'Grid · 2-Col', w: 470, qr: 54, fit: true, desc: 'Many stones · 2 columns' },
  { id: 'grid3', label: 'Grid · 3-Col', w: 690, qr: 54, fit: true, desc: 'Lots of stones · 3 columns' },
];

// Tag sizes (Tag Workspace). Large = original layout. Smaller sizes trim
// whitespace/image most, fonts least, so every field stays readable.
export const TAG_SIZES = [
  { id: 'xsmall', label: 'Extra Small', font: 0.72, space: 0.3, img: 0.42, width: 0.62 },
  { id: 'small', label: 'Small', font: 0.8, space: 0.45, img: 0.55, width: 0.72 },
  { id: 'medium', label: 'Medium', font: 0.9, space: 0.7, img: 0.75, width: 0.86 },
  { id: 'large', label: 'Large', font: 1, space: 1, img: 1, width: 1 },
];
const LARGE = TAG_SIZES[TAG_SIZES.length - 1];
export const tagSizeCfg = (id) => TAG_SIZES.find((z) => z.id === id) || LARGE;
const TagSizeCtx = createContext(LARGE);

// User font scale for tag content (70%–150%), remembered on this device.
const FONT_KEY = 'tag_font_scale';
export const FONT_MIN = 0.7; export const FONT_MAX = 1.5;
let fontScale = (() => { const v = Number(localStorage.getItem(FONT_KEY)); return v >= FONT_MIN && v <= FONT_MAX ? v : 1; })();
const fontSubs = new Set();
export function setTagFontScale(v) {
  fontScale = Math.min(FONT_MAX, Math.max(FONT_MIN, Math.round(v * 10) / 10));
  try { localStorage.setItem(FONT_KEY, String(fontScale)); } catch { /* noop */ }
  fontSubs.forEach((f) => f());
}
export const useTagFontScale = () => useSyncExternalStore((f) => { fontSubs.add(f); return () => fontSubs.delete(f); }, () => fontScale);
const scaled = (z, k) => (k === 1 ? z : { ...z, font: z.font * k });

export function TagFontControl({ testid = 'tag-font' }) {
  const k = useTagFontScale();
  const btn = 'inline-flex h-7 w-8 items-center justify-center text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40';
  return (
    <div className="inline-flex items-center overflow-hidden rounded-md border border-slate-200 bg-white" title="Text size of the tag content" data-testid={testid}>
      <button type="button" className={btn} onClick={() => setTagFontScale(k - 0.1)} disabled={k <= FONT_MIN + 0.001} aria-label="Smaller text" data-testid={`${testid}-decrease`}>A−</button>
      <span className="min-w-[44px] border-x border-slate-200 px-1 text-center text-[11px] tabular-nums text-slate-600" data-testid={`${testid}-value`}>{Math.round(k * 100)}%</span>
      <button type="button" className={btn} onClick={() => setTagFontScale(k + 0.1)} disabled={k >= FONT_MAX - 0.001} aria-label="Larger text" data-testid={`${testid}-increase`}>A+</button>
    </div>
  );
}

// ── Exact label size (mm) ────────────────────────────────────────────────
// The tag is drawn in a fixed W×H mm box. If content overflows, we step down
// through progressively tighter presets (Large → … → Extra Small → Tiny).
// If even Tiny overflows the tag "does not fit": single tags block printing,
// batch tags are printed taller (same width) or skipped — the user's choice.
export const MM_PX = 96 / 25.4;
const FIT_STEPS = [...TAG_SIZES].reverse().concat([{ id: 'tiny', label: 'Tiny', font: 0.64, space: 0.2, img: 0.32, width: 1 }]);
const CUSTOM_KEY = 'tag_custom_mm';
const LAST_SIZE_KEY = 'tag_last_size';
export function loadCustomMm() {
  try {
    const v = JSON.parse(localStorage.getItem(CUSTOM_KEY) || 'null');
    if (v && v.w > 0 && v.h > 0) return v;
  } catch { /* noop */ }
  return { w: 75, h: 45 };
}
export function saveCustomMm(v) {
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(v)); } catch { /* noop */ }
}

// 1 mm safe margin kept empty on every side of a tag.
const SAFE_PX = MM_PX;
// Extra slack so the PDF rasteriser (which measures text slightly wider) never wraps or clips.
const FIT_SLACK = 0.96;
const presetPad = () => (isPublicTagHost() ? '1mm' : 0);
// True when a preset (non-mm) tag would clip or squeeze data.
export const presetTagUnfit = (node) => !!node && (!!node.querySelector('[data-jno-unfit="1"]') || node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1);
const BOX = { padding: SAFE_PX, background: C.white, border: `1px solid ${C.ink}`, borderRadius: 6, overflow: 'hidden', boxSizing: 'border-box', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'normal' };

// Auto layouts tried for custom sizes, per size step (largest text first).
// "c" variants move Gold Purity under the image to save vertical space.
const AUTO_LAYOUTS = [
  { l: 'classic', pu: false }, { l: 'classic', pu: true },
  { l: 'grid2', pu: false }, { l: 'grid2', pu: true },
  { l: 'grid3', pu: false }, { l: 'grid3', pu: true },
];
const LAYOUT_LABEL = { classic: 'Classic', grid2: '2-Column', grid3: '3-Column', hangtag: 'Hang Tag', detailed: 'Detailed' };
const GROW_STEP = 0.05;
const GROW_MAX = 3;

// overflow: 'block' (single tag — report misfit), 'skip' (render nothing),
// 'grow' (enlarge W×H proportionally until everything fits = smallest size).
const overOf = (box, inner) => inner.scrollHeight > (box.clientHeight - 2 * SAFE_PX) * 0.985 || inner.scrollWidth > inner.clientWidth + 1 || !!inner.querySelector('[data-jno-unfit="1"]');

// fontScale ≠ 1: fit at normal text first, then apply the user's scale and re-check (fontOver → may not print).
export function FitTag({ wMm, hMm, layoutProps, overflow = 'block', onFit, forceLayout, fontScale = 1 }) {
  const combos = useMemo(() => {
    const opts = forceLayout && forceLayout !== 'auto' ? [{ l: forceLayout, pu: false }, { l: forceLayout, pu: true }] : AUTO_LAYOUTS;
    return FIT_STEPS.flatMap((z) => opts.map((c) => ({ z, ...c })));
  }, [forceLayout]);
  const [idx, setIdx] = useState(0);
  const [k, setK] = useState(1);
  const [res, setRes] = useState(null);
  const [res2, setRes2] = useState(null);
  const phase2 = !!res && fontScale !== 1;
  const boxRef = useRef(null);
  const innerRef = useRef(null);
  const wm = +(wMm * k).toFixed(1);
  const hm = +(hMm * k).toFixed(1);
  useLayoutEffect(() => {
    const box = boxRef.current; const inner = innerRef.current;
    if (!box || !inner) return;
    if (res) {
      if (!phase2 || res2) return;
      const fo = overOf(box, inner);
      const r2 = { ...res, fits: res.fits && !fo, fontOver: fo };
      setRes2(r2);
      onFit?.(r2);
      return;
    }
    const over = overOf(box, inner);
    if (over && idx < combos.length - 1) {
      // Step synchronously in small chunks, then yield a frame (keeps React's nested-update limit safe).
      if ((idx + 1) % 8 !== 0) { setIdx(idx + 1); return; }
      const raf = requestAnimationFrame(() => setIdx(idx + 1));
      return () => cancelAnimationFrame(raf);
    }
    if (over && overflow === 'grow' && k < GROW_MAX) {
      // Grow on the next frame so each size step is a fresh render (avoids nested-update limits).
      const raf = requestAnimationFrame(() => { setK(+(k + GROW_STEP).toFixed(2)); setIdx(0); });
      return () => cancelAnimationFrame(raf);
    }
    const r = { fits: !over, grown: k > 1, size: combos[idx].z.id, layout: combos[idx].l, purityUnder: combos[idx].pu, wMm: wm, hMm: hm };
    setRes(r);
    if (fontScale === 1) onFit?.(r);
  }, [idx, k, res, res2, phase2, fontScale, onFit, combos, overflow, wm, hm]);
  const final = fontScale !== 1 ? res2 : res;
  if (final && !final.fits && overflow === 'skip') return null;
  const { z, l, pu } = combos[idx];
  return (
    <div ref={boxRef} data-testid="tag-fit-box" data-fits={final ? String(final.fits) : ''} data-font-over={final?.fontOver ? '1' : ''} data-layout={l} data-wmm={wm} data-hmm={hm}
      style={{ ...BOX, position: 'relative', width: Math.round(wm * MM_PX), height: Math.round(hm * MM_PX) }}>
      <div ref={innerRef} style={{ width: '100%' }}>
        <TagSizeCtx.Provider value={{ ...z, font: z.font * (phase2 ? fontScale : 1), width: 1 }}>
          {renderLayout(l, { ...layoutProps, purityUnderImage: pu })}
        </TagSizeCtx.Provider>
      </div>
    </div>
  );
}

function PresetPicker({ value, onPick, testPrefix }) {
  const { mine, company, canPublish } = useLabelPresets();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const same = (p) => p.w === value.w && p.h === value.h;
  const curCompany = company.find(same);
  const curMine = mine.find(same);
  const selected = curCompany ? `c:${curCompany.name}` : curMine ? `m:${curMine.name}` : '';
  const upsert = (list, n) => [...list.filter((p) => p.name.toLowerCase() !== n.toLowerCase()), { name: n, w: value.w, h: value.h }];
  const save = async (toCompany) => {
    const n = name.trim();
    if (!n) { toast.error('Enter a name for this size.'); return; }
    try {
      if (toCompany) await saveCompanyPresets(upsert(company, n));
      else await saveLabelPresets(upsert(mine, n));
      toast.success(toCompany ? `Published "${n}" to the company.` : `Saved "${n}" (${value.w}×${value.h} mm).`);
      setNaming(false); setName('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the size.'); }
  };
  const remove = async () => {
    try {
      if (curCompany && canPublish) { await saveCompanyPresets(company.filter((p) => p !== curCompany)); toast.success(`Removed "${curCompany.name}" from the company.`); }
      else if (curMine) { await saveLabelPresets(mine.filter((p) => p !== curMine)); toast.success(`Removed "${curMine.name}".`); }
    } catch { toast.error('Could not remove the size.'); }
  };
  const onSelect = (v) => {
    const [kind, ...rest] = v.split(':');
    const n = rest.join(':');
    const p = (kind === 'c' ? company : mine).find((x) => x.name === n);
    if (p) onPick({ w: p.w, h: p.h });
  };
  const cls = 'rounded-md border border-slate-200 px-2 py-1 text-[11px]';
  const canRemove = (curCompany && canPublish) || (!curCompany && curMine);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <select value={selected} onChange={(e) => onSelect(e.target.value)} className={`${cls} bg-white`} data-testid={`${testPrefix}-preset-select`} aria-label="Saved label sizes">
        <option value="">{mine.length || company.length ? 'Saved sizes…' : 'No saved sizes'}</option>
        {company.length ? (
          <optgroup label="Company sizes">
            {company.map((p) => <option key={`c${p.name}`} value={`c:${p.name}`}>{p.name} ({p.w}×{p.h})</option>)}
          </optgroup>
        ) : null}
        {mine.length ? (
          <optgroup label="My sizes">
            {mine.map((p) => <option key={`m${p.name}`} value={`m:${p.name}`}>{p.name} ({p.w}×{p.h})</option>)}
          </optgroup>
        ) : null}
      </select>
      {naming ? (
        <>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(false); if (e.key === 'Escape') setNaming(false); }}
            placeholder="Size name" maxLength={40} className={`${cls} w-28`} data-testid={`${testPrefix}-preset-name`} />
          <button type="button" onClick={() => save(false)} className={`${cls} bg-slate-800 text-white`} data-testid={`${testPrefix}-preset-save`}>Save for me</button>
          {canPublish ? <button type="button" onClick={() => save(true)} className={`${cls} bg-violet-600 text-white`} data-testid={`${testPrefix}-preset-publish`}>Publish to company</button> : null}
        </>
      ) : (
        <button type="button" onClick={() => setNaming(true)} className={`${cls} bg-white hover:bg-slate-50`} data-testid={`${testPrefix}-preset-add`}>Save size</button>
      )}
      {canRemove ? <button type="button" onClick={remove} className={`${cls} bg-white text-red-600 hover:bg-red-50`} data-testid={`${testPrefix}-preset-remove`}>Remove</button> : null}
    </span>
  );
}

function CustomMmInputs({ value, onChange, testPrefix }) {
  const commit = (next) => { saveCustomMm(next); onChange(next); };
  const set = (k, v) => commit({ ...value, [k]: Math.max(10, Math.min(300, parseFloat(v) || 0)) });
  const cls = 'w-16 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-mono';
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-[11px] text-slate-500" data-testid={`${testPrefix}-custom-mm`}>
      <input key={`w${value.w}`} type="number" min="10" max="300" step="1" defaultValue={value.w} onBlur={(e) => set('w', e.target.value)} className={cls} data-testid={`${testPrefix}-custom-w`} aria-label="Width (mm)" />
      ×
      <input key={`h${value.h}`} type="number" min="10" max="300" step="1" defaultValue={value.h} onBlur={(e) => set('h', e.target.value)} className={cls} data-testid={`${testPrefix}-custom-h`} aria-label="Height (mm)" />
      mm
      <PresetPicker value={value} onPick={commit} testPrefix={testPrefix} />
    </span>
  );
}
export { CustomMmInputs };
function useSz() {
  const z = useContext(TagSizeCtx);
  return {
    f: (n) => Math.max(6, +(n * z.font).toFixed(2)),
    s: (n) => +(n * z.space).toFixed(1),
    i: (n) => Math.round(n * z.img),
    large: z.id === 'large',
  };
}

// Standalone "Print QR only" sticker sizes, in CSS px (96dpi). Smaller fits
// tiny jewellery tags; larger is easier to scan. Default is Small.
export const QR_LABEL_SIZES = [
  { id: 'xs', label: 'Extra Small', px: 48 },
  { id: 'sm', label: 'Small', px: 64 },
  { id: 'md', label: 'Medium', px: 88 },
  { id: 'lg', label: 'Large', px: 120 },
];

function purityText(name, pct) {
  const n = (name || '').trim();
  if (n && isPublicTagHost()) return n;
  const p = pct != null ? Number(pct) : null;
  if (n && p) return `${n} · ${p.toFixed(2)}%`;
  if (n) return n;
  if (p) return `${p.toFixed(2)}%`;
  return '—';
}

// Normalise a raw material list (already non-gold) to the viewer shape.
function normMaterials(rows) {
  return (rows || []).map((r, i) => ({
    key: `${r.classification_schema || r.packet_no || 'rm'}-${i}`,
    classification: (r.classification_schema || r.packet_no || '—'),
    // Original classification code (tag display names may replace `classification`).
    code: (r.classification_raw || r.classification_schema || r.packet_no || '').trim().toUpperCase(),
    carats: Number(r.carats || 0),
    pieces: Number(r.pieces || 0),
    materialType: r.material_type,
    note: (r.issue_note || '').trim(),
    sieves: (r.sieves || '').trim(),
    rawName: (r.classification_raw || r.classification_schema || r.packet_no || '').trim(),
    shape: (r.shape || '').trim(),
    size: (r.size || '').trim(),
  }));
}

// ── POLKI-SLV handling (TAG DISPLAY ONLY) ────────────────────────────────
// "POLKI-SLV" is a silver-set polki weight captured in carats. On the TAG we
// do NOT list it as a stone line — instead we convert its carats to grams
// (1 ct = 0.2 g, the standard gemological conversion) and fold that into the
// product's Net Weight. This adjustment is applied ONLY here (while building
// the tag) and nowhere else on the portal.
const CT_TO_G = 0.2;
const POLKI_SLV = 'POLKI-SLV';
// On the tag domain, POLKI-DAK is folded into Net Weight the same way.
const netFoldCodes = () => (isPublicTagHost() ? [POLKI_SLV, 'POLKI-DAK'] : [POLKI_SLV]);

function splitPolkiSlv(materials, det) {
  // Tag Domain: the super-admin material handling matrix decides (Dak Wt, mountings, ...).
  if (isPublicTagHost()) return applyMatHandling(materials, tickedFor(det));
  const visible = [];
  let slvCarats = 0;
  const fold = netFoldCodes();
  for (const m of (materials || [])) {
    if (fold.includes(m.code || (m.classification || '').trim().toUpperCase())) {
      slvCarats += Number(m.carats) || 0;
    } else {
      visible.push(m);
    }
  }
  return { visible, extraNetGrams: slvCarats * CT_TO_G, separate: [] };
}

// Build from the authed detail doc (GET /jewellery/{id}).
export function buildTagDataFromDetail(det) {
  const rm = (det.rm_consumed_snapshot || []).filter((r) => r.material_type !== 'gold');
  // Carat uplift (breakage allowance) is already applied by the server to carats and net weight.
  const { visible, extraNetGrams, separate } = splitPolkiSlv(det.is_manual || det.tag_fix?.ordered ? normMaterials(rm) : sortByMaterialHierarchy(normMaterials(rm)), det);
  return {
    _raw: det,
    separate,
    id: det.id,
    jewelleryNo: det.jewellery_no || '—',
    title: det.title || '',
    jewelleryType: det.jewellery_type || '',
    image: det.image || det.fallback_image || '',
    purityText: purityText(det.gold_purity_name, det.gold_purity_percent),
    purityPct: Number(det.gold_purity_percent) || null,
    purityMissing: !!det.purity_missing,
    gross: Number(det.gross_weight_grams || 0),
    net: Number(det.net_weight_grams || 0) + extraNetGrams,
    pageNo: det.page_no || '',
    materials: visible,
    charges: det.extra_charges || [],
    goldsmith: det.goldsmith || '',
    customer: det.customer || '',
    skipRules: !!det.tag_fix?.skip_rules || !!det.is_manual, // tag rules apply only to main portal pieces
    isManual: !!det.is_manual,
    keepOrder: !!det.tag_fix?.ordered,
    joinNames: det.tag_join || [],
  };
}

// Build from the public endpoint payload (GET /jewellery/public-tag/{id}).
export function buildTagDataFromPublic(pub) {
  const { visible, extraNetGrams, separate } = splitPolkiSlv(pub.is_manual || pub.tag_ordered ? normMaterials(pub.materials) : sortByMaterialHierarchy(normMaterials(pub.materials)));
  return {
    separate,
    id: pub.id,
    jewelleryNo: pub.jewellery_no || '—',
    title: pub.title || '',
    jewelleryType: pub.jewellery_type || '',
    image: pub.image || '',
    purityText: purityText(pub.gold_purity_name, pub.gold_purity_percent),
    purityPct: Number(pub.gold_purity_percent) || null,
    purityMissing: !!pub.purity_missing,
    gross: Number(pub.gross_weight_grams || 0),
    net: Number(pub.net_weight_grams || 0) + extraNetGrams,
    pageNo: pub.page_no || '',
    materials: visible,
    skipRules: !!pub.is_manual,
    isManual: !!pub.is_manual,
    keepOrder: !!pub.tag_ordered,
    joinNames: pub.tag_join || [],
  };
}

// Collapse: total carats + pieces per classification name (notes dropped —
// collapsed is a per-classification summary).
export function collapseMaterials(materials) {
  const map = new Map();
  for (const m of materials) {
    const key = m.classification;
    if (!map.has(key)) {
      map.set(key, { key, classification: m.classification, materialType: m.materialType, carats: 0, pieces: 0, note: '', lines: 0 });
    }
    const g = map.get(key);
    g.carats += Number(m.carats || 0);
    g.pieces += Number(m.pieces || 0);
    g.lines += 1;
  }
  return Array.from(map.values());
}

// Lines shown on a tag for a view; the Tag Domain applies the super-admin tag rules. 'priced' = expanded split per name + note (rate lines).
export function tagMaterials(data, rmMode, rulesOverride) {
  // Manual tags keep the order the lines were entered in.
  const lines = tagMaterialsRaw(data, rmMode, rulesOverride);
  return data?.isManual ? lines : strictTagOrder(lines);
}
function tagMaterialsRaw(data, rmMode, rulesOverride) {
  const materials = data?.materials || [];
  if (isPublicTagHost()) {
    // Raw = every line as-is (note, else sieve) with no tag rules.
    if (rmMode === 'raw') return applyTagRules(materials, 'raw', []);
    return applyTagRules(materials, rmMode === 'collapsed' ? 'collapsed' : 'expanded', data.skipRules ? [] : (rulesOverride || getTagRules()), { jewelleryType: data.jewelleryType, purity: data.purityText, manual: !!data.isManual, keepOrder: !!data.keepOrder, join: data.joinNames || [] }, data.isManual || data.keepOrder ? [] : getTagClassOrder(), rmMode === 'priced');
  }
  return rmMode === 'collapsed' ? collapseMaterials(materials) : materials;
}

// ── Shared tag pieces ────────────────────────────────────────────────────
function ImageBox({ src, size }) {
  if (src === null) return null; // image hidden for this view (QR password setting)
  const box = {
    width: size, height: size, flex: '0 0 auto',
    border: `1px solid ${C.line}`, borderRadius: 6, background: C.slate50,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  };
  if (src) {
    return (
      <div style={box}>
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
      </div>
    );
  }
  return (
    <div style={{ ...box, flexDirection: 'column' }}>
      <div style={{ fontSize: 9, color: C.muted, fontFamily: SANS }}>No photo</div>
    </div>
  );
}

function WeightPills({ gross, net, fs = 10 }) {
  const { s } = useSz();
  const pill = { flex: 1, border: `1px solid ${C.line}`, borderRadius: 5, padding: `${s(3)}px ${s(6)}px`, background: C.white };
  const cap = { fontSize: Math.max(6.5, fs - 2), letterSpacing: 0.4, textTransform: 'uppercase', color: C.muted, fontFamily: SANS };
  const val = { fontSize: fs + 1, fontFamily: MONO, color: C.ink, fontWeight: 600 };
  return (
    <div style={{ display: 'flex', gap: s(6) }}>
      <div style={pill}><div style={cap}>Gross</div><div style={val}>{formatGrams(gross)} g</div></div>
      <div style={pill}><div style={cap}>Net</div><div style={val}>{formatGrams(net)} g</div></div>
    </div>
  );
}

// Split a list into `cols` down-first chunks (column 1 gets the first
// ceil(n/cols) entries, etc.) so a multi-column tag reads top-to-bottom like
// the old CSS-columns flow, but with content-sized columns.
function chunkColumns(arr, cols) {
  const n = arr.length;
  if (cols <= 1 || n === 0) return [arr];
  const per = Math.ceil(n / cols);
  const out = [];
  for (let c = 0; c < cols; c += 1) {
    const slice = arr.slice(c * per, c * per + per);
    if (slice.length) out.push(slice);
  }
  return out;
}

function PriceRow({ m, fs }) {
  const { s } = useSz();
  if (m.priceRow === 'missing') {
    return <div data-testid="tag-price-missing-note" style={{ marginTop: s(3), paddingTop: s(3), borderTop: `1px solid ${C.divider}`, fontFamily: SANS, fontSize: fs, lineHeight: 1.35, color: '#b91c1c', fontWeight: 700 }}>Prices not configured: {m.missing.join(', ')}</div>;
  }
  const total = m.priceRow === 'total';
  const val = m.amount == null ? 'rate missing' : `₹${inr(m.amount)}`;
  return (
    <div data-testid={total ? 'tag-price-total' : 'tag-price-row'} style={{ display: 'flex', justifyContent: 'space-between', gap: s(10), marginTop: total ? s(2) : 0, paddingTop: total ? s(3) : 0, borderTop: total ? `1px solid ${C.divider}` : 'none', fontFamily: MONO, fontSize: fs, lineHeight: 1.35, color: C.ink, fontWeight: total ? 700 : 500 }}>
      <span>{m.classification}</span><span style={{ whiteSpace: 'nowrap', color: m.amount == null ? '#b91c1c' : undefined }}>{val}</span>
    </div>
  );
}

// Separate weight (e.g. Dak Wt) printed after the raw materials in its own colour.
function SepRow({ m, fs }) {
  const { s } = useSz();
  const st = m.style || { bold: true, color: true, highlight: true };
  return (
    <div data-testid="tag-sep-row" style={{ display: 'flex', justifyContent: 'space-between', gap: s(10), marginTop: s(2), padding: st.highlight ? `${s(1)}px ${s(4)}px` : 0, borderRadius: 4, background: st.highlight ? `${m.color}14` : 'transparent', border: st.highlight ? `1px solid ${m.color}55` : 'none', fontFamily: MONO, fontSize: fs, lineHeight: 1.35, color: st.color ? m.color : C.ink, fontWeight: st.bold ? 700 : 500, fontStyle: st.italic ? 'italic' : 'normal', breakInside: 'avoid' }}>
      <span>{m.label}</span><span style={{ whiteSpace: 'nowrap' }}>{m.unit === 'g' ? `${formatGrams(m.qty)} g` : `${formatCarats(m.qty)} ct`}</span>
    </div>
  );
}

function MaterialRow({ m, fs: baseFs, showNotes, showPieces }) {
  // Shrink the line slightly when a note is shown beneath it so the tag still fits.
  const fs = showNotes && m.note ? Math.max(6.5, baseFs - 1) : baseFs;
  const { s } = useSz();
  if (m.priceRow) return <PriceRow m={m} fs={baseFs} />;
  if (m.sepRow) return <SepRow m={m} fs={baseFs} />;
  return (
    <div style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', marginBottom: s(3) }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: s(10) }}>
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: matColor(m.materialType), flex: '0 0 auto', marginTop: fs * 0.4 }} />
          <span style={{ fontFamily: MONO, fontWeight: m.matStyle?.bold ? 800 : 600, fontStyle: m.matStyle?.italic ? 'italic' : 'normal', fontSize: fs, lineHeight: 1.35, color: m.matStyle?.color ? m.matStyle.hex : C.ink, background: m.matStyle?.highlight ? `${m.matStyle.hex}1f` : undefined, borderRadius: 3, whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={m.classification} data-testid="tag-material-classification">
            {m.classification}
          </span>
        </span>
        <span style={{ fontFamily: MONO, fontSize: fs, lineHeight: 1.35, color: C.ink, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
          {formatCarats(m.carats)} ct{showPieces && !m.hidePieces ? ` · ${m.pieces} pcs` : ''}{m.amount !== undefined ? ` · ${m.amount == null ? '₹—' : `₹${inr(m.amount)}`}` : ''}
        </span>
      </div>
      {showNotes && m.note ? (
        <div style={{ marginLeft: 10, fontSize: Math.max(6.5, fs - 1.5), color: C.muted, fontStyle: 'italic', fontFamily: SANS, lineHeight: 1.25 }}>
          {m.note}
        </div>
      ) : null}
    </div>
  );
}

// Optional Karigar / Customer line under the materials (ticked at print time only).
const TagPartyCtx = createContext(null);
const PARTY_KEY = { karigar: false, customer: false };
let party = { ...PARTY_KEY };
const partySubs = new Set();
export function setTagParty(patch) { party = { ...party, ...patch }; partySubs.forEach((f) => f()); }
export const useTagParty = () => useSyncExternalStore((f) => { partySubs.add(f); return () => partySubs.delete(f); }, () => party);
const partyOf = (data, p) => ({ karigar: p.karigar ? data?.goldsmith || '' : '', customer: p.customer ? data?.customer || '' : '' });
export const partyKey = (p) => `${p.karigar ? 'k' : ''}${p.customer ? 'c' : ''}`;

export function TagPartyToggles({ testid = 'tag-party' }) {
  const p = useTagParty();
  const box = (k, label) => (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-slate-700" data-testid={`${testid}-${k}`}>
      <input type="checkbox" checked={p[k]} onChange={(e) => setTagParty({ [k]: e.target.checked })} className="h-3.5 w-3.5 accent-[var(--jewellery-icon,#7c3aed)]" data-testid={`${testid}-${k}-checkbox`} />
      {label}
    </label>
  );
  return <div className="inline-flex items-center gap-3 rounded-md border border-slate-200 bg-white px-2 py-1" title="Print these on the tag">{box('karigar', 'Print Karigar')}{box('customer', 'Print Customer')}</div>;
}

// Gold purity, with the ticked Karigar / Customer printed in line (bold, own colours).
function PurityLine({ style, text }) {
  const { s } = useSz();
  const p = useContext(TagPartyCtx);
  if (!p || (!p.karigar && !p.customer)) return <div style={style}>{text}</div>;
  return (
    <div style={{ ...style, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: s(6), rowGap: 0, justifyContent: style.textAlign === 'center' ? 'center' : 'flex-start' }} data-testid="tag-party-row">
      {text ? <span>{text}</span> : null}
      {p.karigar ? <span style={{ color: '#9a3412', fontWeight: 700 }} data-testid="tag-party-karigar">Karigar: {p.karigar}</span> : null}
      {p.customer ? <span style={{ color: '#1d4ed8', fontWeight: 700 }} data-testid="tag-party-customer">{p.customer}</span> : null}
    </div>
  );
}

function MaterialRows(props) {
  return <MaterialRowsList {...props} />;
}

function MaterialRowsList({ materials, fs = 10, showNotes = true, showPieces = true, columns = 1 }) {
  const { s } = useSz();
  if (!materials.length) {
    return <div style={{ fontSize: fs - 1, color: C.muted, fontStyle: 'italic', fontFamily: SANS }}>No stones in this piece.</div>;
  }
  const multi = columns > 1;
  if (multi) {
    // Content-sized columns joined by a thin divider. Each column is only as
    // wide as its widest row (capped), so the classification name and the
    // ct/pcs value stay close instead of being pushed to opposite edges of a
    // fixed-width tag.
    const chunks = chunkColumns(materials, columns);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {chunks.map((col, ci) => (
          <div
            key={ci}
            style={{
              display: 'flex', flexDirection: 'column', flex: '0 0 auto',
              width: 'max-content', maxWidth: 260,
              paddingLeft: ci > 0 ? s(12) : 0, marginLeft: ci > 0 ? s(12) : 0,
              borderLeft: ci > 0 ? `1px solid ${C.divider}` : 'none',
            }}
          >
            {col.map((m) => (
              <MaterialRow key={m.key} m={m} fs={fs} showNotes={showNotes} showPieces={showPieces} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: s(3) }}>
      {materials.map((m) => (
        <MaterialRow key={m.key} m={m} fs={fs} showNotes={showNotes} showPieces={showPieces} />
      ))}
    </div>
  );
}

// Jewellery No. always stays on one line; its font shrinks until it fits
// (with slack). Below 6px it is flagged unfit so the tag is skipped/grown.
const JNO_MIN = 6;
function JewNo({ text, fs, flex }) {
  const boxRef = useRef(null);
  const txtRef = useRef(null);
  useLayoutEffect(() => {
    const box = boxRef.current; const t = txtRef.current;
    if (!box || !t) return;
    let size = fs;
    t.style.fontSize = `${size}px`;
    const tooWide = () => t.offsetWidth > box.clientWidth * FIT_SLACK;
    while (tooWide() && size > JNO_MIN) { size = Math.max(JNO_MIN, +(size - 0.25).toFixed(2)); t.style.fontSize = `${size}px`; }
    box.dataset.jnoUnfit = tooWide() ? '1' : '0';
  });
  return (
    <div ref={boxRef} data-testid="tag-jewellery-no" style={{ ...(flex ? { flex: 1, minWidth: 0 } : {}), overflow: 'hidden', whiteSpace: 'nowrap' }}>
      <span ref={txtRef} style={{ display: 'inline-block', fontFamily: MONO, fontSize: fs, fontWeight: 700, color: C.emerald, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{text}</span>
    </div>
  );
}

function SectionLabel({ children, fs = 8 }) {
  // The tag domain prints the stone list without a "Raw material" heading.
  if (isPublicTagHost()) return null;
  return (
    <div style={{ fontSize: fs, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, fontFamily: SANS, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function QrChip({ value, size }) {
  return (
    <div style={{ background: C.white, padding: size < 40 ? 2 : 3, border: `1px solid ${C.line}`, borderRadius: 5, flex: '0 0 auto' }}>
      <QRCode value={value || ' '} size={size} style={{ height: size, width: size }} />
    </div>
  );
}

// ── Layouts ───────────────────────────────────────────────────────────────
// Each layout reads the chosen tag size (Small / Medium / Large) from context:
// fonts shrink a little, spacing + image shrink more, so nothing is dropped.
function PurityUnder({ data, f, width }) {
  return <PurityLine style={{ fontFamily: SANS, fontSize: f(10), color: C.amber, fontWeight: 600, textAlign: 'center', width, overflowWrap: 'anywhere' }} text={data.purityText} />;
}

function LayoutClassic({ data, materials, showNotes, qr, qrValue, showQr, showPieces, purityUnderImage = false }) {
  const { f, s, i, large } = useSz();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: large ? 192 : undefined }}>
      <div style={{ height: s(5), background: C.violet }} />
      <div style={{ display: 'flex', gap: s(10), padding: s(10), flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s(6), flex: '0 0 auto' }}>
          <ImageBox src={data.image} size={i(showQr ? 82 : 104)} />
          {purityUnderImage ? <PurityUnder data={data} f={f} width={i(showQr ? 82 : 104)} /> : null}
          {showQr ? <QrChip value={qrValue} size={i(qr)} /> : null}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: s(5) }}>
          <JewNo text={data.jewelleryNo} fs={f(15)} />
          {purityUnderImage ? null : <PurityLine style={{ fontFamily: SANS, fontSize: f(10), color: C.amber, fontWeight: 600 }} text={data.purityText} />}
          <WeightPills gross={data.gross} net={data.net} fs={f(9)} />
          <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: s(4), display: 'flex', flexDirection: 'column', gap: s(3), minWidth: 0 }}>
            <SectionLabel fs={f(8)}>Raw material</SectionLabel>
            <MaterialRows materials={materials} fs={f(9.5)} showNotes={showNotes} showPieces={showPieces} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LayoutHangTag({ data, materials, showNotes, qr, qrValue, showQr, showPieces }) {
  const { f, s, i, large } = useSz();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: large ? 336 : undefined }}>
      <div style={{ height: s(5), background: C.violet }} />
      <div style={{ padding: s(10), display: 'flex', flexDirection: 'column', gap: s(7), flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: s(6) }}>
          <JewNo flex text={data.jewelleryNo} fs={f(14)} />
          {showQr ? <QrChip value={qrValue} size={i(qr)} /> : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ImageBox src={data.image} size={i(118)} />
        </div>
        <PurityLine style={{ fontFamily: SANS, fontSize: f(11), color: C.amber, fontWeight: 600, textAlign: 'center' }} text={data.purityText} />
        <WeightPills gross={data.gross} net={data.net} fs={f(10)} />
        <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: s(5), display: 'flex', flexDirection: 'column', gap: s(4), minWidth: 0 }}>
          <SectionLabel fs={f(8)}>Raw material</SectionLabel>
          <MaterialRows materials={materials} fs={f(10)} showNotes={showNotes} showPieces={showPieces} />
        </div>
      </div>
    </div>
  );
}

function LayoutDetailed({ data, materials, showNotes, qr, qrValue, showQr, showPieces }) {
  const { f, s, i, large } = useSz();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: large ? 288 : undefined }}>
      <div style={{ height: s(6), background: C.violet }} />
      <div style={{ display: 'flex', gap: s(12), padding: s(12), flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s(8), flex: '0 0 auto', width: i(118) }}>
          <ImageBox src={data.image} size={i(118)} />
          {showQr ? <QrChip value={qrValue} size={i(qr)} /> : null}
          {showQr ? <div style={{ fontFamily: SANS, fontSize: f(8), color: C.muted, textAlign: 'center', lineHeight: 1.2 }}>Scan for live tag</div> : null}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: s(7) }}>
          <div>
            <JewNo text={data.jewelleryNo} fs={f(18)} />
            {data.title ? <div style={{ fontFamily: SANS, fontSize: f(10), color: C.ink, marginTop: s(3), lineHeight: 1.25 }}>{data.title}</div> : null}
          </div>
          <PurityLine style={{ fontFamily: SANS, fontSize: f(11), color: C.amber, fontWeight: 600 }} text={data.purityText} />
          <WeightPills gross={data.gross} net={data.net} fs={f(10)} />
          <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: s(6), display: 'flex', flexDirection: 'column', gap: s(4), minWidth: 0 }}>
            <SectionLabel fs={f(8)}>Raw material used</SectionLabel>
            <MaterialRows materials={materials} fs={f(10.5)} showNotes={showNotes} showPieces={showPieces} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Column-flow layout for pieces with MANY raw-material entries: a top header
// band (image + identity + weights + optional QR) with the raw-material list
// flowing into `cols` columns below, so the tag stays short vertically.
function LayoutGrid({ data, materials, showNotes, qr, qrValue, showQr, showPieces, cols = 2, purityUnderImage = false }) {
  const { f, s, i } = useSz();
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: s(5), background: C.violet }} />
      <div style={{ padding: s(12), display: 'flex', flexDirection: 'column', gap: s(9) }}>
        <div style={{ display: 'flex', gap: s(12), alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s(3), flex: '0 0 auto' }}>
            <ImageBox src={data.image} size={i(84)} />
            {purityUnderImage ? <PurityUnder data={data} f={f} width={i(84)} /> : null}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: s(4) }}>
            <JewNo text={data.jewelleryNo} fs={f(16)} />
            {purityUnderImage ? null : <PurityLine style={{ fontFamily: SANS, fontSize: f(10), color: C.amber, fontWeight: 600 }} text={data.purityText} />}
            <WeightPills gross={data.gross} net={data.net} fs={f(9)} />
          </div>
          {showQr ? <QrChip value={qrValue} size={i(qr)} /> : null}
        </div>
        <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: s(7), display: 'flex', flexDirection: 'column', gap: s(5), minWidth: 0 }}>
          <SectionLabel fs={f(8)}>Raw material</SectionLabel>
          <MaterialRows materials={materials} fs={f(9.5)} showNotes={showNotes} showPieces={showPieces} columns={cols} />
        </div>
      </div>
    </div>
  );
}

function renderLayoutInner(layoutId, props) {
  if (layoutId === 'hangtag') return <LayoutHangTag {...props} />;
  if (layoutId === 'detailed') return <LayoutDetailed {...props} />;
  if (layoutId === 'grid2') return <LayoutGrid {...props} cols={2} />;
  if (layoutId === 'grid3') return <LayoutGrid {...props} cols={3} />;
  return <LayoutClassic {...props} />;
}
function renderLayout(layoutId, props) {
  return <TagPartyCtx.Provider value={props.party || null}>{renderLayoutInner(layoutId, props)}</TagPartyCtx.Provider>;
}

export const FONT_LINKS = `
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Gloock&display=swap" rel="stylesheet">`;

// Generic print-window opener. `pageSize` accepts any CSS @page size token
// (e.g. '3.5in 2in', 'A4'); `margin` is the @page margin. Shared by the
// single-tag prints and the batch sheet so the print styling stays identical.
export function openPrintWindow(html, { pageSize = 'auto', title = 'Print', margin = '0' } = {}) {
  const win = window.open('', '_blank', 'width=820,height=720');
  if (!win) {
    toast.error('Please allow pop-ups to print.');
    return;
  }
  win.document.write(`<!doctype html><html><head><title>${title}</title>
<meta charset="utf-8" />${FONT_LINKS}
<style>
  @page { size: ${pageSize}; margin: ${margin}; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  ${printFontCss()}
</style></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch (_) { /* noop */ } }, 500);
}

function printNodeHtml(html, pageSize, title) {
  openPrintWindow(html, { pageSize, title, margin: '0' });
}

// Build the public QR target URL for a tag. Priority:
//   1) explicit `origin` override,
//   2) REACT_APP_PUBLIC_TAG_BASE_URL — the neutral white-label domain the
//      printed tags should always point at (e.g. https://metriqs.co.in),
//   3) the current window origin (preview / fallback).
export function buildQrValue(data, origin) {
  const envBase = (process.env.REACT_APP_PUBLIC_TAG_BASE_URL || '').replace(/\/+$/, '');
  const base = origin || envBase || (typeof window !== 'undefined' ? window.location.origin : '');
  return data?.id ? `${base}/t/${data.id}` : (data?.jewelleryNo || ' ');
}

// Pick a sensible DEFAULT layout from how many raw-material rows will show:
//   <= 5  -> Classic Card (compact)
//   6-10  -> Grid 2-Col
//   > 10  -> Grid 3-Col
// The user can still switch freely afterwards.
export function computeDefaultLayout(data, rmMode) {
  if (!data) return 'classic';
  const list = [...tagMaterials(data, rmMode), ...sepRows(data)];
  const n = list.length;
  if (n > 10) return 'grid3';
  if (n > 5) return 'grid2';
  return 'classic';
}

// QR scan page: viewer chooses whether the Excel gets rate / amount columns.
function ExcelChoice({ onDownload, hidden }) {
  const [open, setOpen] = useState(false);
  if (hidden) return null;
  const go = (withRates) => { setOpen(false); Promise.resolve(onDownload(withRates)).catch(() => toast.error('Could not build the Excel file.')); };
  // Public viewers have no rate card, so the Excel is details only.
  if (isPublicTagHost()) {
    return (
      <div className="flex justify-center" data-testid="public-tag-excel">
        <Button variant="outline" onClick={() => go(false)} data-testid="public-tag-excel-button" className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]">
          <FileSpreadsheet size={15} className="mr-1.5" /> Download Excel
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-2" data-testid="public-tag-excel">
      {open ? (
        <>
          <span className="text-xs text-slate-500">Excel with:</span>
          <Button size="sm" onClick={() => go(true)} className="bg-[var(--ink-navy)] text-white hover:bg-[var(--ink-navy-2)]" data-testid="public-tag-excel-with-rates">Rate &amp; amount columns</Button>
          <Button size="sm" variant="outline" onClick={() => go(false)} data-testid="public-tag-excel-details-only">Details only</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="public-tag-excel-cancel">Cancel</Button>
        </>
      ) : (
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="public-tag-excel-button" className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]">
          <FileSpreadsheet size={15} className="mr-1.5" /> Download Excel
        </Button>
      )}
    </div>
  );
}

export function JewelleryTagViewer({ data: dataProp, origin, defaultShowQr = true, piecesToggle = false, rmToggle = true, defaultRmMode = 'expanded', lockPublic = false, sizeToggle = false, initialLayout, onLayoutChange, publicView, hideExcel = false }) {
  // Super-admin line correction: reload this piece after saving so the tag reflects it.
  const [fixedData, setFixedData] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const data = fixedData && fixedData.id === dataProp?.id ? fixedData : dataProp;
  const reloadFixed = async () => {
    setFixOpen(false);
    const { data: res } = await notesApi.post('/notes/tag-workspace/tags/batch', { jewellery_ids: [data.id] });
    if (res?.items?.[0]) setFixedData(buildTagDataFromDetail(res.items[0]));
    window.dispatchEvent(new Event('tagws-pieces-changed'));
  };
  const [sizeId, setSizeIdRaw] = useState(() => (sizeToggle && localStorage.getItem(LAST_SIZE_KEY)) || 'large');
  const setSizeId = (v) => { setSizeIdRaw(v); if (sizeToggle) { try { localStorage.setItem(LAST_SIZE_KEY, v); } catch { /* noop */ } } };
  const [customMm, setCustomMm] = useState(loadCustomMm);
  const [fitRes, setFitRes] = useState(null);
  const [suggest, setSuggest] = useState(null); // smallest proportional size that fits
  const isCustom = sizeToggle && sizeId === 'custom';
  const sizeCfg = tagSizeCfg(sizeToggle && !isCustom ? sizeId : 'large');
  // Public QR-scan view is LOCKED: a single Classic tag only — no layout
  // switcher, no raw-material/pieces/QR toggles, no print actions. It is
  // always collapsed and never shows pieces, and scrolls vertically on a
  // phone when a piece has many raw-material lines.
  const effectiveRmMode = lockPublic ? (['expanded', 'raw'].includes(publicView?.rm_mode) ? publicView.rm_mode : 'collapsed') : defaultRmMode;
  const [layoutId, setLayoutIdRaw] = useState(() => {
    if (lockPublic) return 'classic';
    if (initialLayout && TAG_LAYOUTS.some((l) => l.id === initialLayout)) return initialLayout;
    return computeDefaultLayout(data, defaultRmMode);
  });
  const setLayoutId = (v) => { setLayoutIdRaw(v); onLayoutChange?.(v); };
  const [rmMode, setRmMode] = useState(effectiveRmMode); // 'expanded' | 'collapsed'
  const [showQr, setShowQr] = useState(lockPublic ? false : defaultShowQr);
  const [qrSizeId, setQrSizeId] = useState('sm'); // standalone "Print QR only" size
  // Pieces (pcs) visibility. Portal dialog shows a toggle (default ON); the
  // public QR-scan viewer never shows pieces (locked OFF).
  const [showPieces, setShowPieces] = useState(lockPublic ? !!publicView?.show_pieces : piecesToggle);
  useEffect(() => { setSuggest(null); }, [customMm.w, customMm.h, rmMode, showQr, showPieces]); // eslint-disable-line react-hooks/exhaustive-deps
  const [scale, setScale] = useState(1.3);
  const fontK = useTagFontScale();
  const partySel = useTagParty();
  const partyVal = useMemo(() => (lockPublic ? null : partyOf(data, partySel)), [lockPublic, data, partySel]);
  const [presetOver, setPresetOver] = useState(false);
  const tagRef = useRef(null);
  const qrOnlyRef = useRef(null);
  const qrImgRef = useRef(null);
  const previewRef = useRef(null);

  const layoutCfg = useMemo(() => TAG_LAYOUTS.find((l) => l.id === layoutId) || TAG_LAYOUTS[0], [layoutId]);
  const qrPx = useMemo(() => (QR_LABEL_SIZES.find((q) => q.id === qrSizeId) || QR_LABEL_SIZES[0]).px, [qrSizeId]);
  const qrValue = useMemo(() => buildQrValue(data, origin), [origin, data]);
  const tagRules = useTagRules();
  const pricing = usePricing();
  const goldState = useGoldRate();
  const cardsList = useRateCards();
  const priced = isPublicTagHost() && !lockPublic;
  const baseMaterials = useMemo(() => (data ? tagMaterials(data, rmMode) : []), [data, rmMode, tagRules]); // eslint-disable-line react-hooks/exhaustive-deps
  const materials = useMemo(() => [...(priced ? pricedMaterials(data, rmMode, baseMaterials) : baseMaterials), ...sepRows(data)], [priced, data, rmMode, baseMaterials, pricing, goldState, cardsList]); // eslint-disable-line react-hooks/exhaustive-deps
  const pk = priced ? `${pricing.mode}-${pricing.cardId}-${currentGold(goldState)?.rate || ''}-${rateCardById(pricing.cardId)?.updated_at || ''}-${Object.keys(pricing.stoneRates).length}` : '';
  const showNotes = rmMode === 'expanded' || isPublicTagHost();

  // Fit the preview tag to the available width (responsive on phones).
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;
    const compute = () => {
      const avail = el.clientWidth - 28;
      // Wide / grid ("fit") layouts must never be enlarged — enlarging them
      // overflows the dialog and triggers a zoom/resize growth loop. Narrow
      // layouts may still be enlarged for readability.
      const baseW = isCustom ? customMm.w * MM_PX : layoutCfg.w;
      const maxScale = (!isCustom && (layoutCfg.w >= 500 || layoutCfg.fit)) ? 1 : 1.55;
      const s = Math.min(maxScale, Math.max(0.55, avail / baseW));
      setScale(Number.isFinite(s) && s > 0 ? s : 1);
    };
    compute();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(compute);
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [layoutCfg.w, isCustom, customMm.w]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preset sizes: re-check for clipped content whenever the text size changes.
  useLayoutEffect(() => { if (!isCustom) setPresetOver(fontK !== 1 && presetTagUnfit(tagRef.current)); });
  const fontBlocked = !lockPublic && fontK !== 1 && (isCustom ? !!fitRes?.fontOver : presetOver);
  const handlePrint = async () => {
    const node = tagRef.current;
    if (!node || !data) return;
    if (priced && !(await ensureRates([data]))) return;
    if (fontBlocked) { toast.error('The text is too large for this tag — reduce the text size (A−) to print.'); return; }
    if (isCustom) {
      if (!fitRes?.fits) {
        toast.error(`This tag doesn't fit ${customMm.w}×${customMm.h} mm even at the smallest readable text. Increase the label size or switch to Collapsed / Without pieces.`);
        return;
      }
      printNodeHtml(node.innerHTML, `${customMm.w}mm ${customMm.h}mm`, `Tag · ${data.jewelleryNo}`);
      return;
    }
    if (presetTagUnfit(node)) {
      toast.error(`Tag ${data.jewelleryNo} doesn't fit the ${sizeCfg.label} size — some data would be cut off. Use a larger size or a custom mm size.`);
      return;
    }
    // Grid layouts are content-sized (no fixed physical page). Measure the
    // rendered node (dividing out the preview zoom) to produce a tight page
    // that matches the dynamic tag width. Fixed-size layouts keep their exact
    // physical page sizes.
    let pageSize = sizeCfg.id === 'large' && fontK === 1 ? layoutCfg.page : null;
    if (layoutCfg.fit || !pageSize) {
      const rect = node.getBoundingClientRect();
      const s = scale > 0 ? scale : 1;
      const wIn = ((rect.width / s) + 2) / 96;
      const hIn = ((rect.height / s) + 2) / 96;
      pageSize = `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`;
    }
    printNodeHtml(node.outerHTML, pageSize, `Tag · ${data.jewelleryNo}`);
  };
  const handlePrintQrOnly = () => {
    const node = qrOnlyRef.current;
    if (!node || !data) return;
    // Tight page around the QR + jewellery-no line, so the printed sticker is
    // only as big as the chosen QR size (fits small jewellery tags).
    const wIn = (node.offsetWidth + 2) / 96;
    const hIn = (node.offsetHeight + 2) / 96;
    printNodeHtml(node.outerHTML, `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`, `QR · ${data.jewelleryNo}`);
  };

  const handlePrintQrImage = () => {
    const node = qrImgRef.current;
    if (!node || !data) return;
    // QR + photo side by side, both at the chosen QR size. Tight page.
    const wIn = (node.offsetWidth + 2) / 96;
    const hIn = (node.offsetHeight + 2) / 96;
    printNodeHtml(node.outerHTML, `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`, `QR+Img · ${data.jewelleryNo}`);
  };

  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="jewellery-tag-viewer">
      {!lockPublic && (
      <>
      {/* Layout switcher */}
      {isCustom ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="jewellery-tag-layout-auto">
          Custom size: the layout (Classic, 2-Column or 3-Column) is picked automatically so every detail fits.
        </div>
      ) : (
      <div className="flex flex-wrap gap-2" data-testid="jewellery-tag-layout-tabs">
        {TAG_LAYOUTS.map((l) => {
          const active = l.id === layoutId;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => setLayoutId(l.id)}
              aria-pressed={active}
              data-testid={`jewellery-tag-layout-${l.id}`}
              className={[
                'flex-1 min-w-[108px] rounded-md border px-3 py-2 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]',
                active ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-bg)]' : 'border-slate-200 bg-white hover:border-[var(--jewellery-border)] hover:bg-slate-50',
              ].join(' ')}
            >
              <div className={`text-[12px] font-medium ${active ? 'text-[var(--jewellery-icon)]' : 'text-[var(--ink-navy)]'}`}>{l.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{l.desc}</div>
            </button>
          );
        })}
      </div>
      )}

      {/* Controls row: raw-material view + QR on/off */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="jewellery-tag-rm-mode">
        {rmToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Raw material:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setRmMode('expanded')}
                aria-pressed={rmMode === 'expanded'}
                data-testid="jewellery-tag-rm-expanded"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${rmMode === 'expanded' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <List size={12} /> Expanded
              </button>
              <button
                type="button"
                onClick={() => setRmMode('collapsed')}
                aria-pressed={rmMode === 'collapsed'}
                data-testid="jewellery-tag-rm-collapsed"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${rmMode === 'collapsed' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <Rows3 size={12} /> Collapsed
              </button>
              {isPublicTagHost() ? (
                <button
                  type="button"
                  onClick={() => setRmMode('raw')}
                  aria-pressed={rmMode === 'raw'}
                  data-testid="jewellery-tag-rm-raw"
                  title="Every line as-is, no tag rules"
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${rmMode === 'raw' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  <List size={12} /> Raw
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">QR code:</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowQr(true)}
              aria-pressed={showQr}
              data-testid="jewellery-tag-qr-on"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${showQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <QrCode size={12} /> With QR
            </button>
            <button
              type="button"
              onClick={() => setShowQr(false)}
              aria-pressed={!showQr}
              data-testid="jewellery-tag-qr-off"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${!showQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <Ban size={12} /> No QR
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">QR print size:</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-qr-size">
            {QR_LABEL_SIZES.map((q, i) => (
              <button
                key={q.id}
                type="button"
                onClick={() => setQrSizeId(q.id)}
                aria-pressed={qrSizeId === q.id}
                data-testid={`jewellery-tag-qr-size-${q.id}`}
                className={`px-2.5 py-1 text-[11px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${qrSizeId === q.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        {sizeToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Tag size:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-size">
              {[...TAG_SIZES, { id: 'custom', label: 'Custom (mm)' }].map((z, idx) => (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => { setSizeId(z.id); setFitRes(null); }}
                  aria-pressed={sizeId === z.id}
                  data-testid={`jewellery-tag-size-${z.id}`}
                  className={`px-2.5 py-1 text-[11px] transition-colors ${idx > 0 ? 'border-l border-slate-200' : ''} ${sizeId === z.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  {z.label}
                </button>
              ))}
            </div>
            {isCustom ? <CustomMmInputs value={customMm} onChange={setCustomMm} testPrefix="jewellery-tag" /> : null}
          </div>
        ) : null}

        {piecesToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Pieces:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-pieces">
              <button
                type="button"
                onClick={() => setShowPieces(true)}
                aria-pressed={showPieces}
                data-testid="jewellery-tag-pieces-on"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                With pieces
              </button>
              <button
                type="button"
                onClick={() => setShowPieces(false)}
                aria-pressed={!showPieces}
                data-testid="jewellery-tag-pieces-off"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${!showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Without pieces
              </button>
            </div>
          </div>
        ) : null}
      </div>
      </>
      )}

      {/* Preview */}
      <div
        ref={previewRef}
        className={lockPublic
          ? 'flex items-start justify-center overflow-auto py-2'
          : `relative rounded-lg border border-dashed p-4 flex items-start justify-center min-h-[260px] overflow-auto ${isCustom && fitRes && !fitRes.fits ? `border-red-400 bg-red-50/40 ${fitRes.fontOver ? '' : 'pt-16'}` : 'border-slate-300 bg-slate-100'}`}
        data-testid="jewellery-tag-preview"
      >
        {isCustom && fitRes && !fitRes.fits && !fitRes.fontOver ? (
          <div className="absolute inset-x-3 top-3 z-10 flex flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50/95 px-3 py-2 text-[12px] font-medium text-red-700 shadow-sm" data-testid="jewellery-tag-fit-overlay">
            <span>Data is being cut off — this tag doesn&apos;t fit {customMm.w}×{customMm.h} mm.</span>
            {suggest ? (
              <>
                <span className="text-slate-700" data-testid="jewellery-tag-fit-suggestion">Smallest size that fits: <b>{suggest.w}×{suggest.h} mm</b></span>
                <button
                  type="button"
                  onClick={() => { saveCustomMm(suggest); setCustomMm(suggest); }}
                  className="rounded-md bg-[var(--ink-navy)] px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90"
                  data-testid="jewellery-tag-use-suggested-size"
                >
                  Use this size
                </button>
              </>
            ) : <span className="text-slate-500">Finding the smallest size that fits…</span>}
            <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }}>
              <FitTag
                key={`sg-${customMm.w}x${customMm.h}-${rmMode}-${showQr}-${showPieces}-${pk}`}
                wMm={customMm.w} hMm={customMm.h} overflow="grow"
                onFit={(r) => setSuggest(r.fits ? { w: Math.ceil(r.wMm), h: Math.ceil(r.hMm) } : null)}
                layoutProps={{ data, materials, showNotes, qr: layoutCfg.qr, qrValue, showQr, showPieces, party: partyVal }}
              />
            </div>
          </div>
        ) : null}
        <div style={{ zoom: scale }}>
          {isCustom ? (
            <div ref={tagRef} data-testid="jewellery-tag-render">
              <FitTag
                key={`${customMm.w}x${customMm.h}-${rmMode}-${showQr}-${showPieces}-${pk}-${fontK}-${partyKey(partySel)}`}
                wMm={customMm.w} hMm={customMm.h} onFit={setFitRes} fontScale={lockPublic ? 1 : fontK}
                layoutProps={{ data, materials, showNotes, qr: layoutCfg.qr, qrValue, showQr, showPieces, party: partyVal }}
              />
            </div>
          ) : (
          <div
            ref={tagRef}
            data-testid="jewellery-tag-render"
            style={{ width: layoutCfg.fit ? 'fit-content' : Math.round(layoutCfg.w * sizeCfg.width), maxWidth: Math.round(layoutCfg.w * sizeCfg.width), background: C.white, border: `1px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden', padding: presetPad(), boxSizing: 'border-box', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'tabular-nums slashed-zero', boxShadow: '0 1px 3px rgba(15,23,42,0.12)' }}
          >
            <TagSizeCtx.Provider value={scaled(sizeCfg, lockPublic ? 1 : fontK)}>
              {renderLayout(layoutId, { data, materials, showNotes, qr: layoutCfg.qr, qrValue, showQr, showPieces, party: partyVal })}
            </TagSizeCtx.Provider>
          </div>
          )}
        </div>
      </div>
      {fontBlocked ? (
        <div className="text-[12px] font-bold text-red-600" data-testid="jewellery-tag-font-over">Text at {Math.round(fontK * 100)}% goes outside the tag — reduce the text size (A−) to print.</div>
      ) : isCustom && fitRes ? (
        fitRes.fits ? (
          <div className="text-[11px] text-emerald-700" data-testid="jewellery-tag-fit-ok">Fits {customMm.w}×{customMm.h} mm · layout auto-picked: {LAYOUT_LABEL[fitRes.layout]}{fitRes.purityUnder ? ' · purity under image' : ''}{fitRes.size !== 'large' ? ` · text ${FIT_STEPS.find((z) => z.id === fitRes.size)?.label}` : ''}.</div>
        ) : (
          <div className="text-[11px] text-red-600" data-testid="jewellery-tag-fit-error">Doesn&apos;t fit {customMm.w}×{customMm.h} mm in Classic, 2-Column or 3-Column even at the smallest readable text — increase the size or use Collapsed / Without pieces.</div>
        )
      ) : null}

      {!lockPublic && rmMode !== 'raw' ? <TagMergePreview materials={baseMaterials} onFix={data?._raw?.tag_fix ? () => setFixOpen(true) : undefined} fixed={!!data?._raw?.tag_fix?.active} /> : null}
      {!lockPublic && data?._raw?.can_reorder ? <div className="flex justify-end"><button type="button" onClick={() => setReorderOpen(true)} className="flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2 py-1 text-[11px] font-semibold text-indigo-800 hover:bg-indigo-100" data-testid="tag-reorder-open"><ArrowUpDown size={12} /> Reorder lines</button></div> : null}
      {reorderOpen ? <TagReorderLinesDialog data={data} onClose={() => setReorderOpen(false)} onSaved={() => { setReorderOpen(false); reloadFixed().catch(() => {}); }} /> : null}
      {fixOpen ? <TagLineFixDialog data={data} onClose={() => setFixOpen(false)} onSaved={() => reloadFixed().catch(() => {})} /> : null}

      {/* Actions */}
      {lockPublic ? <ExcelChoice hidden={hideExcel} onDownload={(withRates) => downloadTagsExcel([{ data, materials }], { rmMode, showPieces, withRates, fileName: `${data.jewelleryNo}.xlsx` })} /> : null}
      {!lockPublic && (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!lockPublic ? <><TagMatOptToggles testid="jewellery-tag-mat-opts" pieces={data?._raw ? [data._raw] : []} /><TagPartyToggles testid="jewellery-tag-party" /></> : null}
        <TagFontControl testid="jewellery-tag-font" />
        {isPublicTagHost() ? <PricingControls up testid="jewellery-tag-pricing" /> : null}
        {isPublicTagHost() ? (
        <Button
          variant="outline"
          onClick={async () => { if (!(await ensureRates([data]))) return; downloadTagsExcel([{ data, materials: baseMaterials }], { rmMode, showPieces, withRates: pricingOn(), rates: rateCardById(pricing.cardId), fileName: `${data.jewelleryNo}.xlsx` }).catch(() => toast.error('Could not build the Excel file.')); }}
          data-testid="jewellery-tag-excel-button"
          className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
        >
          <FileSpreadsheet size={15} className="mr-1.5" /> Download Excel
        </Button>
        ) : null}
        <Button
          variant="outline"
          onClick={handlePrintQrOnly}
          data-testid="jewellery-tag-print-qr-button"
          className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
        >
          <QrCode size={15} className="mr-1.5" /> Print QR only
        </Button>
        <Button
          variant="outline"
          onClick={handlePrintQrImage}
          data-testid="jewellery-tag-print-qr-image-button"
          className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
        >
          <QrCode size={15} className="mr-1.5" /> Print QR + Image
        </Button>
        <Button
          onClick={handlePrint}
          disabled={(isCustom && !fitRes?.fits) || fontBlocked}
          title={(isCustom && !fitRes?.fits) || fontBlocked ? 'Printing is disabled until all the data fits the chosen size' : undefined}
          data-testid="jewellery-tag-print-button"
          className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white"
        >
          <Printer size={15} className="mr-1.5" /> Print tag
        </Button>
      </div>
      )}

      {/* Off-screen QR-only node (for Print QR only) */}
      <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
        <div
          ref={qrOnlyRef}
          style={{ width: 'max-content', minWidth: qrPx + 28, boxSizing: 'border-box', padding: 10, background: C.white, textAlign: 'center', fontFamily: SANS, fontVariantNumeric: 'tabular-nums slashed-zero' }}
        >
          <div style={{ display: 'inline-block', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald, whiteSpace: 'nowrap', padding: '0 2px' }}>{data.jewelleryNo}</div>
        </div>
      </div>

      {/* Off-screen QR + Image node (for Print QR + Image) — QR and photo side
          by side, the photo sized to match the chosen QR size. */}
      <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
        <div
          ref={qrImgRef}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 10, background: C.white, fontVariantNumeric: 'tabular-nums slashed-zero' }}
        >
          <div style={{ display: 'inline-flex', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <ImageBox src={data.image} size={qrPx} />
        </div>
      </div>
    </div>
  );
}

export default JewelleryTagViewer;

// ── Batch sheet item ───────────────────────────────────────────────────────
// One print-ready item for the batch sheet. `mode` picks the output:
//   • 'full'  — the complete tag card (auto-sized), QR included
//   • 'qr'    — a QR-only sticker with the jewellery number
//   • 'qrimg' — QR + photo side by side with the jewellery number
// Items render as inline-blocks with `break-inside: avoid` so they flow onto
// an A4 sheet, multiple per page, without ever splitting across a page break.
export function BatchTagItem({
  data,
  mode = 'full',
  layoutId = 'classic',
  rmMode = 'expanded',
  showPieces = false,
  showQr = true,
  qrPx = 64,
  origin,
  sizeId = 'large',
  customMm,
  overflow = 'grow',
  onFit,
  rules,
  priced = false,
  fontScale = 1,
}) {
  useTagRules();
  const partySel = useTagParty();
  const partyVal = useMemo(() => partyOf(data, partySel), [data, partySel]);
  usePricing();
  useGoldRate();
  useRateCards();
  if (!data) return null;
  const qrValue = buildQrValue(data, origin);
  const wrap = {
    display: 'inline-block', verticalAlign: 'top',
    breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid',
    margin: 6,
  };

  if (mode === 'qr') {
    return (
      <div style={wrap}>
        <div style={{ width: 'max-content', minWidth: qrPx + 28, boxSizing: 'border-box', padding: 10, background: C.white, textAlign: 'center', fontFamily: SANS, border: `1px solid ${C.line}`, borderRadius: 8, fontVariantNumeric: 'normal' }}>
          <div style={{ display: 'inline-block', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald, whiteSpace: 'nowrap', padding: '0 2px' }}>{data.jewelleryNo}</div>
        </div>
      </div>
    );
  }

  if (mode === 'qrimg') {
    return (
      <div style={wrap}>
        <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 8, padding: 10, textAlign: 'center', fontVariantNumeric: 'normal' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'inline-flex', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
              <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
            </div>
            <ImageBox src={data.image} size={qrPx} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald, whiteSpace: 'nowrap', padding: '0 2px' }}>{data.jewelleryNo}</div>
        </div>
      </div>
    );
  }

  // Full tag card.
  const cfg = TAG_LAYOUTS.find((l) => l.id === layoutId) || TAG_LAYOUTS[0];
  const materials = [...(isPublicTagHost() && priced ? pricedMaterials(data, rmMode, tagMaterials(data, rmMode, rules)) : tagMaterials(data, rmMode, rules)), ...sepRows(data)];
  const showNotes = rmMode === 'expanded' || isPublicTagHost();
  if (sizeId === 'custom' && customMm) {
    return (
      <div style={{ ...wrap, margin: 0, pageBreakAfter: 'always', breakAfter: 'page' }} data-custom-tag="1">
        <FitTag
          wMm={customMm.w} hMm={customMm.h} overflow={overflow} forceLayout={layoutId} fontScale={fontScale}
          onFit={onFit ? (r) => onFit(data.id, r) : undefined}
          layoutProps={{ data, materials, showNotes, qr: cfg.qr, qrValue, showQr, showPieces, party: partyVal }}
        />
      </div>
    );
  }
  const z = tagSizeCfg(sizeId);
  const w = Math.round(cfg.w * z.width);
  return (
    <div style={wrap}>
      <div data-testid="tag-preset-box" style={{ width: cfg.fit ? 'fit-content' : w, maxWidth: w, background: C.white, border: `1px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden', padding: presetPad(), boxSizing: 'border-box', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'normal' }}>
        <TagSizeCtx.Provider value={scaled(z, fontScale)}>
          {renderLayout(layoutId, { data, materials, showNotes, qr: cfg.qr, qrValue, showQr, showPieces, party: partyVal })}
        </TagSizeCtx.Provider>
      </div>
    </div>
  );
}
